import * as React from "react";
import { Button } from "../Button/Button";
import { Text } from "../Text/Text";
import Styles from "./ConfirmModal.module.scss";

export interface ConfirmModalProps {
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Color of the confirm button; destructive actions stay red. */
  confirmColor?: "red" | "gc-blue";
  confirmLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * GameChanger-style confirmation dialog: centered card, X to dismiss, Cancel
 * bottom-left, the (destructive) action bottom-right.
 */
export const ConfirmModal = ({
  title,
  message,
  confirmLabel,
  cancelLabel = "Cancel",
  confirmColor = "red",
  confirmLoading = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps) => {
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  return (
    <div className={Styles.overlay} onClick={onCancel} role="presentation">
      <div
        className={Styles.card}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={Styles.header}>
          <button type="button" className={Styles.close} onClick={onCancel} aria-label="Close">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
          <Text weight="xbold" color="offBlack" className={Styles.title}>
            {title}
          </Text>
        </div>
        <div className={Styles.body}>{message}</div>
        <div className={Styles.footer}>
          <Button title={cancelLabel} color="gc-blue" onClick={onCancel} data-testid="confirm-modal-cancel" />
          <Button
            title={confirmLabel}
            color={confirmColor}
            loading={confirmLoading}
            onClick={onConfirm}
            data-testid="confirm-modal-confirm"
          />
        </div>
      </div>
    </div>
  );
};
